/**
 * German phone number validation utility.
 *
 * Rules:
 * 1. Must have at least 10 digits (excluding formatting characters)
 * 2. Must not contain 5+ consecutive ascending or descending digits
 * 3. Must start with 0 or 49 (after stripping non-digits, leading +, and leading 00)
 */

export const stripNonDigits = (number) => number.replace(/\D/g, '');

/**
 * Rule 1: Minimum 10 digits after stripping non-digit formatting characters.
 */
export const hasMinimumDigits = (number) => {
  const digits = stripNonDigits(number);
  return digits.length >= 10;
};

/**
 * Rule 2: No 5 or more consecutive ascending or descending digits.
 * e.g. 12345 (ascending), 98765 (descending)
 */
export const hasNoSequentialDigits = (number) => {
  const digits = stripNonDigits(number).split('').map(Number);
  const SEQUENCE_LENGTH = 5;

  return !digits.some((_, startIndex) => {
    if (startIndex + SEQUENCE_LENGTH > digits.length) return false;
    const consecutiveDigits = digits.slice(
      startIndex,
      startIndex + SEQUENCE_LENGTH
    );
    return (
      consecutiveDigits.every(
        (digit, position) =>
          position === 0 || digit === consecutiveDigits[position - 1] + 1
      ) ||
      consecutiveDigits.every(
        (digit, position) =>
          position === 0 || digit === consecutiveDigits[position - 1] - 1
      )
    );
  });
};

/**
 * Rule 3: After stripping leading '+' and non-digits, the number must start with 0 or 49.
 * If it starts with 00 (international access code), strip the 00 first, then check.
 */
export const hasValidPrefix = (number) => {
  let cleaned = number.replace(/^\+/, '');
  cleaned = stripNonDigits(cleaned);

  if (cleaned.startsWith('00')) {
    cleaned = cleaned.substring(2);
  }

  return cleaned.startsWith('0') || cleaned.startsWith('49');
};

/**
 * Combined German phone validation.
 * @returns {{ isValid: boolean, failedRule: string | null }}
 */
export const validateGermanPhone = (number) => {
  if (!hasMinimumDigits(number)) {
    return { isValid: false, failedRule: 'minDigits' };
  }
  if (!hasNoSequentialDigits(number)) {
    return { isValid: false, failedRule: 'sequentialDigits' };
  }
  if (!hasValidPrefix(number)) {
    return { isValid: false, failedRule: 'invalidPrefix' };
  }
  return { isValid: true, failedRule: null };
};

/**
 * German phone validation for Advanced Phone Inputs.
 * Validates digit count and sequential digits on the ORIGINAL number (without prefix),
 * but uses the normalized number (with +49 prepended) for prefix validation.
 * This prevents short numbers from passing validation when +49 is prepended.
 *
 * @param {string} originalNumber - The original number entered by the user (without country code)
 * @param {string} normalizedNumber - The number with +49 prepended for prefix validation
 * @returns {{ isValid: boolean, failedRule: string | null }}
 */
export const validateGermanPhoneAdvanced = (
  originalNumber,
  normalizedNumber
) => {
  if (!hasMinimumDigits(originalNumber)) {
    return { isValid: false, failedRule: 'minDigits' };
  }
  if (!hasNoSequentialDigits(originalNumber)) {
    return { isValid: false, failedRule: 'sequentialDigits' };
  }
  if (!hasValidPrefix(normalizedNumber)) {
    return { isValid: false, failedRule: 'invalidPrefix' };
  }
  return { isValid: true, failedRule: null };
};
